import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import useUsers from '../../users/hooks/useUsers';
import { useSpaces } from '../../gatherings/hooks/data/useSpaces';
import useCheckIns from '../hooks/useCheckIns';
import CheckInsTable, { EnrichedCheckIn } from './CheckInsTable';
import CheckInsStats from './CheckInsStats';
import UserDetailModal from '../../users/components/UserDetailModal';
import { searchCheckIns } from '../../../services/api/endpoints/checkins';
import { CheckInResponse } from '../../../types';
import { downloadCSV } from '../../../utils/csvExport';

const CheckInsPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Fetch users and spaces for enrichment
  const { allUsers } = useUsers(user?.token || null);
  const { spaces } = useSpaces(user?.token || null);

  // Helper to get date 30 days ago
  const getDefaultFromDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
  };

  const getDefaultToDate = () => {
    return new Date().toISOString().split('T')[0]; // YYYY-MM-DD (today)
  };

  // Date filters (default: last 30 days)
  const [fromDate, setFromDate] = useState<string | undefined>(getDefaultFromDate());
  const [toDate, setToDate] = useState<string | undefined>(getDefaultToDate());
  
  // Space filter
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | undefined>(undefined);

  // Frontend pagination state
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Fetch check-ins with backend date filtering (default: last 30 days)
  const { allData, isLoading, error, refresh } = useCheckIns(
    user?.token || null,
    fromDate,
    toDate
  );
  
  // Filter on frontend by space only (date filtering is done on backend)
  const filteredData = allData.filter(checkIn => {
    // Space filter
    if (selectedSpaceId && checkIn.space_id !== selectedSpaceId) return false;
    return true;
  });

  // Calculate pagination on frontend using filtered data
  const totalCount = filteredData.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Enrich check-ins with membership and notes data from allUsers
  const enrichedCheckIns: EnrichedCheckIn[] = paginatedData.map(checkIn => {
    const fullUser = allUsers.find(u => u.id === checkIn.user_id);
    return {
      ...checkIn,
      membershipTier: fullUser?.usage_data?.membership_tier || undefined,
      notes_count: fullUser?.notes_count
    };
  });

  const handleViewUser = (userId: string) => {
    setSelectedUserId(userId);
  };

  const handleCloseModal = () => {
    setSelectedUserId(null);
  };

  // Format timestamp to MM/DD/YYYY for display
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Format timestamp with time for CSV export
  const formatTimestampWithTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}/${day}/${year} ${hours}:${minutes}`;
  };

  // Format quota type for CSV
  const formatQuotaType = (quotaType: string): string => {
    if (quotaType === 'day_pass') return 'Day Pass';
    if (quotaType === 'membership') return 'Membership';
    return quotaType;
  };

  // Export filtered check-ins to CSV
  const handleExportCSV = async () => {
    if (!user?.token) return;
    
    setIsExporting(true);
    setExportError(null);
    
    try {
      const csvData = filteredData.map(checkIn => {
        const userName = checkIn.user 
          ? [checkIn.user.first_name, checkIn.user.last_name].filter(Boolean).join(' ').trim() || checkIn.user.display_name || checkIn.user.email
          : checkIn.user_id;
        
        const userEmail = checkIn.user?.email || 'N/A';
        const spaceName = checkIn.space?.name || checkIn.space_id;
        
        // Find membership tier from allUsers
        const fullUser = allUsers.find(u => u.id === checkIn.user_id);
        const membershipTier = fullUser?.usage_data?.membership_tier || 'No membership';
        
        return {
          'User': userName,
          'Email': userEmail,
          'Membership': membershipTier,
          'Space': spaceName,
          'Date': formatTimestampWithTime(checkIn.timestamp),
          'Check In Type': formatQuotaType(checkIn.used_quota_type)
        };
      });
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `check-ins-all-${timestamp}.csv`;
      
      // Download CSV
      downloadCSV(csvData, filename);
      
    } catch (err) {
      console.error('Failed to export check-ins:', err);
      setExportError(err instanceof Error ? err.message : 'Failed to export check-ins');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="check-ins-container">
      <div className="check-ins-content">
        <h2>Check-Ins</h2>

        {/* Filters Section */}
        <div className="filters-section">
          {/* Location Pills */}
          <div className="filter-group filter-group-full">
            <label>Location:</label>
            <div className="location-pills">
              <button
                className={`location-pill ${!selectedSpaceId ? 'active' : ''}`}
                onClick={() => {
                  setSelectedSpaceId(undefined);
                  setPage(1);
                }}
              >
                All Locations
                <span className="pill-count">{allData.filter(ci => {
                  const utcDate = new Date(ci.timestamp);
                  const estDateStr = utcDate.toLocaleDateString('en-CA', { 
                    timeZone: 'America/New_York',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  });
                  if (fromDate && estDateStr < fromDate) return false;
                  if (toDate && estDateStr > toDate) return false;
                  return true;
                }).length}</span>
              </button>
              {spaces.map((space) => {
                const spaceCheckIns = allData.filter(ci => {
                  if (ci.space_id !== space.id) return false;
                  const utcDate = new Date(ci.timestamp);
                  const estDateStr = utcDate.toLocaleDateString('en-CA', { 
                    timeZone: 'America/New_York',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  });
                  if (fromDate && estDateStr < fromDate) return false;
                  if (toDate && estDateStr > toDate) return false;
                  return true;
                }).length;
                return (
                  <button
                    key={space.id}
                    className={`location-pill ${selectedSpaceId === space.id ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedSpaceId(space.id);
                      setPage(1);
                    }}
                  >
                    {space.name}
                    <span className="pill-count">{spaceCheckIns}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="filter-group">
            <label htmlFor="from-date">From:</label>
            <input
              type="date"
              id="from-date"
              value={fromDate || ''}
              onChange={(e) => setFromDate(e.target.value || undefined)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="to-date">To:</label>
            <input
              type="date"
              id="to-date"
              value={toDate || ''}
              onChange={(e) => setToDate(e.target.value || undefined)}
              className="filter-input"
            />
          </div>

          <button
            onClick={() => {
              setFromDate(undefined);
              setToDate(undefined);
              setSelectedSpaceId(undefined);
            }}
            className="clear-filters-btn"
            disabled={!fromDate && !toDate && !selectedSpaceId}
            title="Clear all filters to show all check-ins"
          >
            Clear Filters
          </button>
          
          <button
            onClick={handleExportCSV}
            className="export-csv-btn"
            disabled={isExporting || isLoading || totalCount === 0}
            title={`Export ${totalCount} check-ins to CSV`}
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>

        {/* Stats Cards */}
        <CheckInsStats 
          checkIns={filteredData} 
          isLoading={isLoading}
          showSpaceBreakdown={false} // Location breakdown now shown in pills
        />

        <div className="check-ins-info">
          {!isLoading && totalCount > 0 && (
            <p className="results-info">
              Showing {paginatedData.length} of {totalCount} total check-ins
            </p>
          )}
          {exportError && (
            <p className="export-error" style={{ color: 'red', marginTop: '10px' }}>
              Export failed: {exportError}
            </p>
          )}
        </div>

        <CheckInsTable
          checkIns={enrichedCheckIns}
          isLoading={isLoading}
          error={error}
          onViewUser={handleViewUser}
        />

        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="pagination-btn"
            >
              ← Previous
            </button>
            <span className="pagination-info">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      <UserDetailModal
        open={selectedUserId !== null}
        onClose={handleCloseModal}
        userId={selectedUserId}
      />
    </div>
  );
};

export default CheckInsPage;
